// Copyright ©️ 2026 eVolve MEP, LLC

import { formatDate, type InsightsDetailsWorkOrder, type WorkOrder } from '@evolvemep/api-ts';
import type { ColDef, ValueFormatterParams } from 'ag-grid-enterprise';

import { CellRenderer } from 'components/Mantine/CellRenderer';
import { hoursColDef } from 'components/Mantine/commonColumnDefs';
import { convertSecondsToDuration } from 'components/Mantine/durationHelpers';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { lateColor } from 'constants/badgeMappingStatus';
import { getInsightsScheduleColDefs } from 'helpers/ag-grid/getScheduleColDefs';
import { isNotNil } from 'helpers/isNotNil';
import { convertMantineColorToCss } from 'styles/theme/useGenerateMantineTheme';

export const getWorkOrderColumnDefs = (): ColDef<InsightsDetailsWorkOrder>[] => {
  const { needByColDef, daysRemainingColDef, daysEarlyLateColDef } = getInsightsScheduleColDefs();
  return [
    {
      field: 'workOrderIdentifier',
      headerName: 'Work Order Id',
      lockVisible: true,
      pinned: true,
      cellRenderer: CellRenderer<InsightsDetailsWorkOrder>(({ value, data }) => (
        <EvolveLink to={`/work-orders/${data.workOrderId}`} from="Insights">
          {value}
        </EvolveLink>
      )),
    },
    {
      field: 'projectName',
    },
    {
      field: 'facilityName',
    },
    {
      field: 'workOrderName',
    },
    {
      headerName: 'Status',
      field: 'workOrderStateName',
      cellRenderer: CellRenderer<InsightsDetailsWorkOrder>(({ data }) => (
        <EntityStatusBadge
          customStatusEntityName="Work Order"
          stateName={data.workOrderStateName}
          statusId={data.workOrderStatusId}
        />
      )),
    },
    {
      field: 'estimatedTimeInSeconds',
      headerName: 'Estimated Time',
      ...hoursColDef(),
    },
    {
      field: 'workOrderTotalTimeInSeconds',
      headerName: 'Total Time',
      headerTooltip: 'Does not include actively running tasks',
      ...hoursColDef(),
    },
    {
      colId: 'estimatedVsTotal',
      headerName: 'Estimated vs Total',
      headerTooltip:
        'Calculates difference between Estimated and Total time; value is displayed in red when total exceeds estimated',
      valueGetter: ({ data }) =>
        data?.workOrderTotalTimeInSeconds && data.estimatedTimeInSeconds
          ? data.workOrderTotalTimeInSeconds - data.estimatedTimeInSeconds
          : null,
      valueFormatter: ({ value }: { value: number | null }) => (value ? (value / 60 / 60).toFixed(2) : ''),
      cellRenderer: CellRenderer<WorkOrder, never, number | null>(({ value: estimatedVsTotal }) =>
        isNotNil(estimatedVsTotal)
          ? `${estimatedVsTotal > 0 ? '+' : '–'} ${convertSecondsToDuration(Math.abs(estimatedVsTotal))}`
          : '',
      ),
      cellStyle: ({ value: estimatedVsTotal }) =>
        isNotNil(estimatedVsTotal)
          ? {
              color: estimatedVsTotal > 0 ? convertMantineColorToCss(lateColor) : 'green',
            }
          : undefined,
      width: 180,
      sortable: false,
    },
    needByColDef,
    {
      field: 'onSiteDate',
      headerName: 'On-site Date',
      width: 125,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<InsightsDetailsWorkOrder, InsightsDetailsWorkOrder['onSiteDate']>) =>
        formatDate(value, 'MM/dd/yyyy'),
    },
    daysRemainingColDef,
    {
      field: 'workOrderDescription',
      headerName: 'Comments',
      tooltipField: 'workOrderDescription',
    },
    {
      field: 'dateApproved',
      valueFormatter: ({ value }) => formatDate(value, 'MM/dd/yyyy', '--'),
    },
    {
      field: 'dateInProgress',
      valueFormatter: ({ value }) => formatDate(value, 'MM/dd/yyyy', '--'),
    },
    {
      field: 'dateCompleted',
      valueFormatter: ({ value }) => formatDate(value, 'MM/dd/yyyy', '--'),
    },
    daysEarlyLateColDef,
    {
      field: 'manufacturingLeadTimeDays',
    },
    {
      field: 'productionLeadTimeDays',
    },
  ];
};
